import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import errors from "../utils/errors"
import messages from "../utils/messages"
import { socket, validateNewPasswords, host } from "../utils/utils"
import wallpaper from "@/images/backgrounds/wallpaper-signup.webp"
import CustomCheckbox from "../Components/CustomCheckbox"
import { Link } from "react-router-dom"
import AuthLayout, {
  AuthPasswordToggle,
  authGhostButton,
  authInlineLink,
  authInput,
  authMutedText,
  authPrimaryButton,
  authSubtleLink,
} from "../Components/AuthLayout"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import { setState } from "../store/globalSlice"
import { useState } from "react"
import { Navigate } from "react-router-dom"
import avatar0 from "@/images/avatars/avatar0.webp"
import avatar1 from "@/images/avatars/avatar1.webp"
import avatar2 from "@/images/avatars/avatar2.webp"
import avatar3 from "@/images/avatars/avatar3.webp"
import avatar4 from "@/images/avatars/avatar4.webp"
import avatar5 from "@/images/avatars/avatar5.webp"
import avatar6 from "@/images/avatars/avatar6.webp"
import avatar7 from "@/images/avatars/avatar7.webp"

interface RegisterProps {}

const Register: React.FC<RegisterProps> = () => {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector((state) => state.global.isLoggedIn)
  const showRegisterOtp = useAppSelector(
    (state) => state.global.showRegisterOtp
  )
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState(avatar0)
  const [selectedAvatar, setSelectedAvatar] = useState(0)
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)
  const nav = useNavigate()

  const avatars = [
    avatar0,
    avatar1,
    avatar2,
    avatar3,
    avatar4,
    avatar5,
    avatar6,
    avatar7,
  ]

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setProfilePhoto(file)
      setPhotoPreview(URL.createObjectURL(file))
      setSelectedAvatar(-1) // Clear avatar selection when custom photo is uploaded
    }
  }

  const handleAvatarSelect = (index: number) => {
    setSelectedAvatar(index)
    setProfilePhoto(null)
    setPhotoPreview(avatars[index])
  }

  const sendOtp = () => {
    socket.emit("sendOtp", { otp, email }, (res, token) => {
      if (res) {
        dispatch(setState({ isLoggedIn: true }))
        localStorage.setItem("token", token)
        nav({ pathname: "/lobby" })
        Swal.fire({
          ...messages.registerSuccess,
          icon: "success",
          heightAuto: false,
        })
      } else {
        Swal.fire({
          ...errors.auth.wrongOtp,
          icon: "error",
          heightAuto: false,
        })
      }
    })
  }

  const onRegister = () => {
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !username.trim() ||
      !password.trim() ||
      !confirmPassword.trim() ||
      !email.trim()
    ) {
      Swal.fire({
        ...errors.auth.allFieldRequired,
        icon: "error",
        heightAuto: false,
      })
      return
    }
    if (username.length < 3) {
      Swal.fire({
        ...errors.auth.shortUserName,
        icon: "error",
        heightAuto: false,
      })
      return
    }
    if (username.length > 20) {
      Swal.fire({
        ...errors.auth.longUserName,
        icon: "error",
        heightAuto: false,
      })
      return
    }
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
    if (!emailRegex.test(email)) {
      Swal.fire({
        ...errors.auth.invalidEmail,
        icon: "error",
        heightAuto: false,
      })
      return
    }
    // The checkbox used to be collected and then ignored, which left the
    // consent it claims to record meaning nothing. Now that the policy it
    // points at is a real page, the account waits for the tick.
    if (!agreeToTerms) {
      Swal.fire({
        title: "One more thing",
        text: "Please agree to Deal Master's privacy policy to create an account.",
        icon: "error",
        heightAuto: false,
      })
      return
    }
    const newPasswordsValidated = validateNewPasswords(password, confirmPassword)
    if (!newPasswordsValidated) {
      return
    }

    // Call final registration directly
    onFinalRegister()
  }

  const onFinalRegister = async () => {
    setIsRegistering(true)

    try {
      // Create FormData for multipart/form-data
      const formData = new FormData()
      formData.append("username", username)
      formData.append("email", email)
      formData.append("password", password)
      formData.append("confirmPassword", confirmPassword)

      // Handle profile image - either custom photo or selected avatar
      if (profilePhoto) {
        // Custom uploaded photo
        formData.append("profileImage", profilePhoto)
      } else if (selectedAvatar >= 0) {
        // Selected avatar - convert to file
        try {
          const avatarUrl = avatars[selectedAvatar]
          const response = await fetch(avatarUrl)
          const blob = await response.blob()
          // The built-in avatars are WebP — see the same conversion in
          // ProfileSettings for why the real type has to travel with them.
          const type = blob.type || "image/webp"
          const avatarFile = new File([blob], `avatar${selectedAvatar}.webp`, {
            type,
          })
          formData.append("profileImage", avatarFile)
        } catch (avatarError) {
          console.error("Failed to convert avatar to file:", avatarError)
          // Continue without avatar if conversion fails
        }
      }

      const response = await fetch(`${host}/api/auth/register`, {
        method: "POST",
        body: formData,
      })

      const data = await response.json()

      if (response.ok) {
        // Registration successful
        if (data.otpSent) {
          dispatch(setState({ showRegisterOtp: true }))
        } else {
          // dispatch(setState({ isLoggedIn: true }))
          localStorage.setItem("user", JSON.stringify(data))

          // localStorage.setItem("token", data.token)
          nav("/lobby")
        }
        Swal.fire({
          title: "Registration Successful!",
          text: data.message,
          icon: "success",
          heightAuto: false,
        })
      } else {
        // Registration failed
        Swal.fire({
          title: "Registration Failed",
          text: data.error || "Something went wrong. Please try again.",
          icon: "error",
          heightAuto: false,
        })
      }
    } catch (error) {
      console.error("Registration error:", error)
      Swal.fire({
        title: "Registration Failed",
        text: "Network error. Please check your connection and try again.",
        icon: "error",
        heightAuto: false,
      })
    } finally {
      setIsRegistering(false)
    }
  }

  const resentOtp = () => {
    socket.emit("resentOtp", { email })
  }
  if (isLoggedIn) {
    return <Navigate to="/lobby"></Navigate>
  }

  return (
    <>
      <AuthLayout
        wide={!showRegisterOtp}
        wallpaper={wallpaper}
        marketingTitle="The best game experience"
        marketingBody={
          <>
            <div>
              Join the fun and start playing the world's most popular board game
              online.
            </div>
            <div>
              Buy properties, collect rent, and build an empire that outlasts
              everyone at the table.
            </div>
          </>
        }
        marketingCta={{
          to: "/login",
          label: "Login",
          testId: "go-to-login",
        }}
        heading={showRegisterOtp ? "Verify email" : "Sign up"}
        // Six fields, an avatar row and a consent tick already spend the whole
        // height of a landscape phone; the welcome line is what gives.
        hideSubheadingOnShort={!showRegisterOtp}
        subheading={
          showRegisterOtp
            ? "We sent a code to your email. Enter it below to finish signing up."
            : "Create an account and take your seat at the table."
        }
        footer={
          showRegisterOtp ? null : (
            <div
              className={`mt-8 hidden text-center sm:mt-4 mobile:block ${authMutedText}`}
            >
              Already have an account?{" "}
              {/* No `go-to-login` test id here: the marketing half already
                  carries it, and two nodes under one id is a trap for the
                  first selector that goes looking. */}
              <Link to="/login" className={authInlineLink}>
                Log in
              </Link>
            </div>
          )
        }
      >
        {!showRegisterOtp ? (
          <form
            id="register-form"
            onSubmit={(e) => {
              e.preventDefault()
              if (!isRegistering) onRegister()
            }}
            // Two columns once the screen is short, for the same reason the
            // login form splits: stacked, eight controls do not fit a landscape
            // phone without scrolling.
            className="sm:grid sm:grid-cols-2 sm:items-start sm:gap-8 sm:mobile:!gap-5"
          >
            <div className="flex flex-col gap-3 sm:gap-2">
              <div className="flex items-center gap-3 sm:gap-2">
                <input
                  placeholder="First Name"
                  data-testid="register_first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={authInput}
                ></input>
                <input
                  placeholder="Last Name"
                  data-testid="register_last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={authInput}
                ></input>
              </div>
              <input
                placeholder="Username"
                data-testid="username"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={authInput}
              ></input>
              <input
                placeholder="Email"
                data-testid="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={authInput}
              ></input>
            </div>

            <div className="mt-3 flex flex-col gap-3 sm:mt-0 sm:gap-2">
              {/* `.input1` bakes in `px-6`, and it outranks Tailwind here, so
                  the room for the eye button has to be claimed with `!`. */}
              <div className="relative">
                <input
                  placeholder="Password"
                  data-testid="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${authInput} !pr-16 mobile:!pr-24`}
                  type={showPassword ? "text" : "password"}
                ></input>
                <AuthPasswordToggle
                  open={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              </div>
              <div className="relative">
                <input
                  placeholder="Confirm Password"
                  data-testid="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`${authInput} !pr-16 mobile:!pr-24`}
                  type={showConfirmPassword ? "text" : "password"}
                ></input>
                <AuthPasswordToggle
                  open={showConfirmPassword}
                  onToggle={() => setShowConfirmPassword((v) => !v)}
                />
              </div>

              {/* Profile picture. A row rather than the centred block it used
                  to be, so it costs one line in a column that is already
                  fighting for height on a landscape phone. */}
              <div className="flex items-center gap-4 rounded-3xl border border-brown-300 bg-brown-200/60 px-4 py-3 mobile:border-white/20 mobile:bg-white/10 sm:py-2">
                <div
                  className="h-16 w-16 shrink-0 cursor-pointer overflow-hidden rounded-full border-4 border-red-500 transition-colors hover:border-red-600 mobile:h-24 mobile:w-24 mobile:border-amber-400/60 sm:h-12 sm:w-12"
                  onClick={() => setIsProfileDialogOpen(true)}
                >
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <div className={authMutedText}>Your table portrait</div>
                  <button
                    type="button"
                    onClick={() => setIsProfileDialogOpen(true)}
                    data-testid="register.change-picture-btn"
                    className={authSubtleLink}
                  >
                    Change picture
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={agreeToTerms}
                  aria-label="I agree to Deal Master's privacy policy"
                  data-testid="register.agree-checkbox"
                  onClick={() => setAgreeToTerms(!agreeToTerms)}
                  // The box CustomCheckbox draws is 10px across once index.css
                  // halves the root font on a phone. Now that the tick decides
                  // whether the account can be made, the button around it
                  // carries the 44px target and scales the box to match. The
                  // box itself takes no clicks, or it would toggle twice.
                  className="flex shrink-0 cursor-pointer items-center justify-center p-1 mobile:min-h-[44px] mobile:min-w-[44px]"
                >
                  <span className="pointer-events-none mobile:scale-[1.8]">
                    <CustomCheckbox
                      checked={agreeToTerms}
                      onChange={(checked) => setAgreeToTerms(checked)}
                    ></CustomCheckbox>
                  </span>
                </button>
                <div className={authMutedText}>
                  <span
                    className="cursor-pointer"
                    onClick={() => setAgreeToTerms(!agreeToTerms)}
                  >
                    I agree to Deal Master's{" "}
                  </span>
                  <Link
                    to="/privacy-policy"
                    data-testid="privacy-policy-link"
                    className={authInlineLink}
                  >
                    privacy policy
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isRegistering}
                data-testid="register-btn"
                className={`${authPrimaryButton} mt-2`}
              >
                {isRegistering ? "Creating Account..." : "Create Account"}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              data-testid="otp-input"
              type="text"
              inputMode="numeric"
              className={`${authInput} text-center tracking-[0.4em]`}
              placeholder="Enter OTP"
            />
            <button
              onClick={sendOtp}
              data-testid="otp-btn"
              className={authPrimaryButton}
            >
              Verify OTP
            </button>
            <div className={`mt-2 text-center ${authMutedText}`}>
              Haven't received the email?
            </div>
            <button
              onClick={resentOtp}
              data-testid="resend-otp-btn"
              className={authGhostButton}
            >
              Resend OTP
            </button>
          </div>
        )}
      </AuthLayout>

      {/* Profile Image Selection Dialog */}
      {isProfileDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setIsProfileDialogOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-[28px] border border-white/20 bg-black/80 px-6 py-6 shadow-[0_24px_70px_rgba(0,0,0,0.65)] backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-third text-xl font-bold text-primary mobile:text-4xl">
                Choose your picture
              </h3>
              <button
                onClick={() => setIsProfileDialogOpen(false)}
                className="cursor-pointer text-2xl text-primary/60 transition-colors hover:text-primary mobile:text-5xl"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="flex flex-col gap-6">
              <div className="flex justify-center">
                <div
                  className="h-32 w-32 cursor-pointer overflow-hidden rounded-full border-4 border-amber-400/60 transition-colors hover:border-amber-300"
                  onClick={() => document.getElementById("photo-input")?.click()}
                >
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              <input
                id="photo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />

              <div>
                <h4 className={`mb-3 text-center ${authMutedText}`}>
                  Or select an avatar
                </h4>
                <div className="grid grid-cols-4 gap-3">
                  {avatars.map((avatar, index) => (
                    <div
                      key={index}
                      className={`h-16 w-16 cursor-pointer overflow-hidden rounded-full border-2 transition-all ${
                        selectedAvatar === index && !profilePhoto
                          ? "scale-110 border-amber-400"
                          : "border-white/20 hover:border-amber-400/60"
                      }`}
                      onClick={() => handleAvatarSelect(index)}
                    >
                      <img
                        src={avatar}
                        alt={`Avatar ${index}`}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className={`text-center ${authMutedText}`}>
                Tap the big picture to upload your own, or pick one above.
              </div>

              <button
                onClick={() => setIsProfileDialogOpen(false)}
                className={authPrimaryButton}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Register
